import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router';
import { COMPARISONS } from '@crm/shared/contracts';
import { parseBusinessDate, type BusinessDate } from '@crm/shared/time';
import { useToday } from './clock.tsx';
import { checkPeriod, DEFAULT_PRESET, presetPeriod } from './periods.ts';

export type Comparison = (typeof COMPARISONS)[number];

/** Everything the global controls decide. It lives in the URL: shareable, back-button friendly. */
export interface DashboardParams {
  from: BusinessDate;
  to: BusinessDate;
  compare: Comparison;
  salespersonId: string[];
  sourceId: string[];
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function tryDate(value: string | null): BusinessDate | null {
  if (!value) return null;
  try {
    return parseBusinessDate(value);
  } catch {
    return null;
  }
}

/**
 * Reads the controls from a query string. Anything missing or malformed falls back to a default
 * (the last 30 days compared with the previous period), so a hand-edited URL never breaks the page.
 */
export function parseDashboardParams(
  search: URLSearchParams,
  today: BusinessDate,
): DashboardParams {
  const fallback = presetPeriod(DEFAULT_PRESET, today);
  const from = tryDate(search.get('from'));
  const to = tryDate(search.get('to'));
  const usable = from && to && checkPeriod({ from, to }) === null;
  const period = usable ? { from, to } : fallback;
  if (!period) throw new Error('The default preset always has a period');

  const compare = search.get('compare');
  const ids = (name: string): string[] => [
    ...new Set(search.getAll(name).filter((id) => UUID.test(id))),
  ];
  return {
    from: period.from,
    to: period.to,
    compare: (COMPARISONS as readonly string[]).includes(compare ?? '')
      ? (compare as Comparison)
      : 'previous_period',
    salespersonId: ids('salespersonId'),
    sourceId: ids('sourceId'),
  };
}

/** Writes the controls onto a query string, keeping any parameters it does not own. */
export function applyDashboardParams(
  base: URLSearchParams,
  params: Partial<DashboardParams>,
): URLSearchParams {
  const next = new URLSearchParams(base);
  const set = (name: string, value: string | undefined): void => {
    if (value === undefined) next.delete(name);
    else next.set(name, value);
  };
  if (params.from !== undefined) set('from', params.from);
  if (params.to !== undefined) set('to', params.to);
  if (params.compare !== undefined) set('compare', params.compare);
  for (const name of ['salespersonId', 'sourceId'] as const) {
    const values = params[name];
    if (values === undefined) continue;
    next.delete(name);
    for (const value of values) next.append(name, value);
  }
  return next;
}

/** The query string that carries the period and filters to another page. */
export function carryOver(search: URLSearchParams): string {
  const kept = new URLSearchParams();
  for (const name of ['from', 'to', 'compare', 'salespersonId', 'sourceId']) {
    for (const value of search.getAll(name)) kept.append(name, value);
  }
  const text = kept.toString();
  return text ? `?${text}` : '';
}

export function useDashboardParams(): [
  DashboardParams,
  (change: Partial<DashboardParams>) => void,
] {
  const [search, setSearch] = useSearchParams();
  const today = useToday();
  const params = useMemo(() => parseDashboardParams(search, today), [search, today]);
  const update = useCallback(
    (change: Partial<DashboardParams>) => {
      setSearch((current) => applyDashboardParams(current, change), { replace: false });
    },
    [setSearch],
  );
  return [params, update];
}
